import { Box, Button, Stack, Text, useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { IconInbox } from '@tabler/icons-react';
import { forwardRef, ReactNode } from 'react';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  gradientVariant?: GradientVariant;
  size?: 'sm' | 'md' | 'lg';
}

const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      icon,
      title,
      description,
      actionLabel,
      onAction,
      gradientVariant = 'primary',
      size = 'md',
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const gradient = theme.other.gradients[gradientVariant];

    const sizes = {
      sm: { icon: 40, title: 'sm', desc: 'xs', padding: 'sm' },
      md: { icon: 64, title: 'lg', desc: 'sm', padding: 'xl' },
      lg: { icon: 96, title: 'xl', desc: 'md', padding: '3rem' },
    };

    const s = sizes[size];

    return (
      <Stack ref={ref} align="center" gap="md" p={s.padding}>
        <Box
          style={{
            width: s.icon,
            height: s.icon,
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${gradient.from}20, ${gradient.to}20)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon || (
            <IconInbox
              size={s.icon * 0.5}
              style={{
                color: gradient.from,
                opacity: 0.8,
              }}
            />
          )}
        </Box>

        <Stack align="center" gap={4}>
          <Text
            size={s.title as any}
            fw={600}
            style={{
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {title}
          </Text>
          {description && (
            <Text size={s.desc as any} c="dimmed" ta="center" maw={400}>
              {description}
            </Text>
          )}
        </Stack>

        {actionLabel && onAction && (
          <Button
            onClick={onAction}
            style={{
              backgroundImage: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`,
              border: 'none',
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Stack>
    );
  }
);

EmptyState.displayName = 'EmptyState';

export default EmptyState;
